export type ProjectMappedLine = {
  projectcode?: string;
  dim1?: string;
  dim2?: string;
  dim3?: string;
  dim4?: string;
  dim5?: string;
  warehouse?: string;
};

// Project-controlled dimensions replace the previous project's values. WBS
// (dim3) is item-controlled and is deliberately preserved.
export function lineWithProjectDefaults<T extends ProjectMappedLine>(
  line: T, projectCode: string, project: Record<string, any>, auxiliary: string,
  costCenterFallback = ''
): T {
  return {
    ...line,
    projectcode: projectCode,
    dim1: String(project.U_Division ?? '').trim(),
    dim2: String(project.U_Department ?? '').trim() || costCenterFallback,
    dim4: auxiliary,
    dim5: String(project.U_LOC ?? '').trim(),
    warehouse: String(project.U_WH ?? '').trim(),
  };
}

/** The header project's values that every Create PR line gets. */
export type HeaderProjectDefaults = {
  dim1?: string;
  dim2?: string;
  dim4?: string;
  dim5?: string;
  warehouse?: string;
};

// Create PR, header project. When the user picks it, every line gets the project and its values
// (a value the project lacks is cleared; the line keeps its warehouse then). A saved PR being opened
// keeps what its lines were saved with: only empty fields are filled.
export function linesWithHeaderProject<T extends ProjectMappedLine>(
  lines: T[], projectCode: string, defaults: HeaderProjectDefaults, keepSaved: boolean
): T[] {
  if (keepSaved) {
    const fill = (own: string | undefined, project: string | undefined) => own || project || own;
    return lines.map((line) => ({
      ...line,
      projectcode: fill(line.projectcode, projectCode),
      dim1: fill(line.dim1, defaults.dim1),
      dim2: fill(line.dim2, defaults.dim2),
      dim4: fill(line.dim4, defaults.dim4),
      dim5: fill(line.dim5, defaults.dim5),
      warehouse: fill(line.warehouse, defaults.warehouse),
    }));
  }
  return lines.map((line) => ({
    ...line,
    projectcode: projectCode,
    dim1: defaults.dim1 || undefined,
    dim2: defaults.dim2 || undefined,
    dim4: defaults.dim4 || undefined,
    dim5: defaults.dim5 || undefined,
    warehouse: defaults.warehouse || line.warehouse || undefined,
  }));
}

// An item's own Auxiliary (SAP item field U_AUX_Code) beats a project's: the item's G/L account asks
// for it (Engie KSA: 6201000071 Staff accommodation = AC0013). `itemAuxiliary` gives the item's, or
// nothing when it has none or is not known yet; then the line keeps what it has.
export function withItemAuxiliary<T extends ProjectMappedLine & { itemcode?: string }>(
  line: T, itemAuxiliary: (itemCode: string) => string | null | undefined
): T {
  const code = String(line.itemcode || '').trim();
  const aux = code ? String(itemAuxiliary(code) || '').trim() : '';
  return aux ? { ...line, dim4: aux } : line;
}

// Create PR, header cost center: a line without a project gets it as its department. A saved PR
// being opened keeps a line's own department.
export function linesWithHeaderCostCenter<T extends ProjectMappedLine>(
  lines: T[], costCenter: string, keepSaved: boolean
): T[] {
  return lines.map((line) => line.projectcode ? line : ({ ...line, dim2: (keepSaved && line.dim2) || costCenter }));
}
